import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  deleteDoc, 
  Timestamp,
  writeBatch,
  updateDoc
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytesResumable, 
  getDownloadURL, 
  deleteObject,
  listAll,
  getMetadata
} from 'firebase/storage';
import { db, storage } from './config';
import { compressionService, CompressionOptions, CompressionResult } from '../compression/compressionService';

export interface FileReference {
  id: string;
  url: string;
  path: string;
  filename: string;
  originalFilename: string;
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
  mimeType: string;
  entityType: 'user' | 'deal' | 'loan' | 'consultantLoan' | 'development' | 'reservation' | 'temp';
  entityId: string;
  subType?: string; // e.g., 'profile', 'payment_proof', 'otp', 'fica'
  uploadedBy: string;
  uploadedAt: Timestamp;
  lastAccessedAt?: Timestamp;
  markedForDeletion?: boolean;
  tempExpiresAt?: Timestamp; // For temporary files
}

export interface UploadOptions extends CompressionOptions {
  entityType: FileReference['entityType'];
  entityId: string;
  subType?: string;
  uploadedBy: string;
  onProgress?: (progress: number) => void;
  onCompressionProgress?: (progress: number) => void;
  onUploadProgress?: (progress: number) => void;
}

export class FileManagementService {
  private static instance: FileManagementService;
  private tempCleanupInterval: NodeJS.Timeout | null = null;
  private initialized = false;

  private constructor() {
    // DON'T start cleanup here - wait for explicit initialization after auth
    // This prevents Firebase permission errors on app load before user is authenticated
  }

  static getInstance(): FileManagementService {
    if (!FileManagementService.instance) {
      FileManagementService.instance = new FileManagementService();
    }
    return FileManagementService.instance;
  }

  /**
   * Initialize the service - should only be called AFTER user is authenticated.
   * This starts the temp file cleanup which requires Firestore access.
   */
  initialize(): void {
    if (this.initialized) return;
    this.initialized = true;
    this.startTempCleanup();
  }

  /**
   * Upload a file with compression and reference tracking
   */
  async uploadFile(
    file: File,
    options: UploadOptions
  ): Promise<{ url: string; fileReference: FileReference; compressionResult: CompressionResult }> {
    try {
      // Step 1: Compress the file
      options.onProgress?.(10);
      options.onCompressionProgress?.(0);
      
      const compressionResult = await compressionService.compressFile(file, {
        level: options.level || 'balanced',
        onProgress: (progress) => {
          options.onCompressionProgress?.(progress);
          // Compression is 0-40% of total progress
          options.onProgress?.(10 + (progress * 0.3));
        }
      });

      const compressedFile = compressionResult.file;
      
      // Step 2: Generate storage path
      const storagePath = this.generateStoragePath(
        options.entityType,
        options.entityId,
        compressedFile.name,
        options.subType
      );

      // Step 3: Upload to Firebase Storage
      options.onProgress?.(40);
      options.onUploadProgress?.(0);
      
      const storageRef = ref(storage, storagePath);
      const uploadTask = uploadBytesResumable(storageRef, compressedFile);

      // Track upload progress
      const url = await new Promise<string>((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            options.onUploadProgress?.(progress);
            // Upload is 40-90% of total progress
            options.onProgress?.(40 + (progress * 0.5));
          },
          (error) => {
            console.error('Upload error:', error);
            reject(error);
          },
          async () => {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(downloadURL);
          }
        );
      });

      // Step 4: Create file reference in Firestore
      options.onProgress?.(90);
      
      const fileReference: any = {
        id: doc(collection(db, 'fileReferences')).id,
        url,
        path: storagePath,
        filename: compressedFile.name,
        originalFilename: file.name,
        originalSize: file.size,
        compressedSize: compressedFile.size,
        compressionRatio: compressionResult.compressionRatio,
        mimeType: compressedFile.type,
        entityType: options.entityType,
        entityId: options.entityId,
        subType: options.subType,
        uploadedBy: options.uploadedBy,
        uploadedAt: Timestamp.now()
      };

      // Only add tempExpiresAt for temp files
      if (options.entityType === 'temp') {
        fileReference.tempExpiresAt = Timestamp.fromDate(new Date(Date.now() + 24 * 60 * 60 * 1000));
      }

      await setDoc(doc(db, 'fileReferences', fileReference.id), fileReference);
      
      options.onProgress?.(100);

      return {
        url,
        fileReference,
        compressionResult
      };
    } catch (error) {
      console.error('File upload failed:', error);
      throw error;
    }
  }

  /**
   * Upload multiple files
   */
  async uploadFiles(
    files: File[],
    options: UploadOptions
  ): Promise<Array<{ url: string; fileReference: FileReference; compressionResult: CompressionResult }>> {
    const results = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileProgress = (progress: number) => {
        const overallProgress = ((i / files.length) + (progress / 100 / files.length)) * 100;
        options.onProgress?.(overallProgress);
      };
      
      const result = await this.uploadFile(file, {
        ...options,
        onProgress: fileProgress,
        onCompressionProgress: options.onCompressionProgress,
        onUploadProgress: options.onUploadProgress
      });
      
      results.push(result);
    }
    
    return results;
  }

  /**
   * Generate storage path based on entity type
   */
  private generateStoragePath(
    entityType: FileReference['entityType'],
    entityId: string,
    filename: string,
    subType?: string
  ): string {
    const timestamp = Date.now();
    const sanitizedFilename = filename.replace(/[^a-zA-Z0-9.-]/g, '_');
    const uniqueFilename = `${timestamp}_${sanitizedFilename}`;

    switch (entityType) {
      case 'user':
        return subType 
          ? `users/${entityId}/${subType}/${uniqueFilename}`
          : `users/${entityId}/${uniqueFilename}`;
      
      case 'deal':
        return subType
          ? `deals/${entityId}/${subType}/${uniqueFilename}`
          : `deals/${entityId}/${uniqueFilename}`;
      
      case 'loan':
        return subType
          ? `loans/${entityId}/${subType}/${uniqueFilename}`
          : `loans/${entityId}/${uniqueFilename}`;

      case 'consultantLoan':
        return subType
          ? `consultantLoans/${entityId}/${subType}/${uniqueFilename}`
          : `consultantLoans/${entityId}/${uniqueFilename}`;

      case 'development':
        return subType
          ? `developments/${entityId}/${subType}/${uniqueFilename}`
          : `developments/${entityId}/${uniqueFilename}`;
      
      case 'reservation':
        return subType
          ? `reservations/${entityId}/${subType}/${uniqueFilename}`
          : `reservations/${entityId}/${uniqueFilename}`;
      
      case 'temp':
        return `temp/${entityId}/${uniqueFilename}`;
      
      default:
        return `misc/${entityId}/${uniqueFilename}`;
    }
  }

  /**
   * Delete a file and its reference
   */
  async deleteFile(fileReferenceId: string): Promise<void> {
    try {
      // Get the file reference
      const fileRef = await getDoc(doc(db, 'fileReferences', fileReferenceId));
      if (!fileRef.exists()) {
        throw new Error('File reference not found');
      }

      const fileData = fileRef.data() as FileReference;

      // Delete from Storage
      const storageRef = ref(storage, fileData.path);
      await deleteObject(storageRef);

      // Delete from Firestore
      await deleteDoc(doc(db, 'fileReferences', fileReferenceId));
    } catch (error) {
      console.error('Failed to delete file:', error);
      throw error;
    }
  }

  /**
   * Delete all files for an entity
   */
  async deleteEntityFiles(entityType: FileReference['entityType'], entityId: string): Promise<void> {
    try {
      // Query all file references for this entity
      const q = query(
        collection(db, 'fileReferences'),
        where('entityType', '==', entityType),
        where('entityId', '==', entityId)
      );
      
      const snapshot = await getDocs(q);
      const batch = writeBatch(db);
      const deletePromises: Promise<void>[] = [];

      snapshot.docs.forEach(doc => {
        const fileData = doc.data() as FileReference;
        
        // Delete from Storage
        const storageRef = ref(storage, fileData.path);
        deletePromises.push(deleteObject(storageRef).catch(console.error));
        
        // Mark for deletion in batch
        batch.delete(doc.ref);
      });

      // Execute all deletions
      await Promise.all(deletePromises);
      await batch.commit();
    } catch (error) {
      console.error('Failed to delete entity files:', error);
      throw error;
    }
  }

  /**
   * Get all file references for an entity
   */
  async getEntityFiles(
    entityType: FileReference['entityType'], 
    entityId: string,
    subType?: string
  ): Promise<FileReference[]> {
    try {
      let q = query(
        collection(db, 'fileReferences'),
        where('entityType', '==', entityType),
        where('entityId', '==', entityId)
      );

      if (subType) {
        q = query(q, where('subType', '==', subType));
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as FileReference));
    } catch (error) {
      console.error('Failed to get entity files:', error);
      throw error;
    }
  }

  /**
   * Update last accessed timestamp
   */
  async updateLastAccessed(fileReferenceId: string): Promise<void> {
    try {
      await updateDoc(doc(db, 'fileReferences', fileReferenceId), {
        lastAccessedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Failed to update last accessed:', error);
    }
  }

  /**
   * Clean up temporary files older than 24 hours
   * IMPORTANT: Excludes development documents that were mistakenly stored in temp
   */
  private async cleanupTempFiles(): Promise<number> {
    try {
      const now = Timestamp.now();
      const q = query(
        collection(db, 'fileReferences'),
        where('entityType', '==', 'temp'),
        where('tempExpiresAt', '<', now)
      );

      const snapshot = await getDocs(q);
      const batch = writeBatch(db);
      const deletePromises: Promise<void>[] = [];
      let deletedCount = 0;

      snapshot.docs.forEach(doc => {
        const fileData = doc.data() as FileReference;

        // CRITICAL: Skip cleanup if this is a development document in temp folder
        // This prevents accidental deletion of development documents
        if (fileData.path && fileData.path.includes('development')) {
          console.warn(`Skipping cleanup of development document in temp folder: ${fileData.path}`);
          return;
        }
        
        // Delete from Storage
        const storageRef = ref(storage, fileData.path);
        deletePromises.push(
          deleteObject(storageRef)
            .then(() => { deletedCount++; })
            .catch(console.error)
        );
        
        // Mark for deletion in batch
        batch.delete(doc.ref);
      });

      await Promise.all(deletePromises);
      await batch.commit();

      console.log(`Cleaned up ${deletedCount} temporary files`);
      return deletedCount;
    } catch (error) {
      console.error('Temp file cleanup failed:', error);
      return 0;
    }
  }

  /**
   * Start automatic temp file cleanup
   */
  private startTempCleanup(): void {
    // Run cleanup every hour
    this.tempCleanupInterval = setInterval(() => {
      this.cleanupTempFiles();
    }, 60 * 60 * 1000); // 1 hour

    // Also run immediately on startup
    this.cleanupTempFiles();
  }

  /**
   * Stop temp file cleanup (for cleanup)
   */
  stopTempCleanup(): void {
    if (this.tempCleanupInterval) {
      clearInterval(this.tempCleanupInterval);
      this.tempCleanupInterval = null;
    }
  }

  /**
   * Find orphaned files (files without valid entity references)
   */
  async findOrphanedFiles(): Promise<FileReference[]> {
    try {
      const orphaned: FileReference[] = [];
      const fileRefs = await getDocs(collection(db, 'fileReferences'));
      
      for (const fileDoc of fileRefs.docs) {
        const fileData = fileDoc.data() as FileReference;
        
        // Skip temp files (they have their own cleanup)
        if (fileData.entityType === 'temp') continue;
        
        // Check if the entity still exists
        let entityExists = false;
        
        try {
          const entityRef = doc(db, this.getCollectionName(fileData.entityType), fileData.entityId);
          const entityDoc = await getDoc(entityRef);
          entityExists = entityDoc.exists();
        } catch (error) {
          console.error(`Error checking entity ${fileData.entityType}/${fileData.entityId}:`, error);
        }
        
        if (!entityExists) {
          orphaned.push({ ...fileData, id: fileDoc.id });
        }
      }
      
      return orphaned;
    } catch (error) {
      console.error('Failed to find orphaned files:', error);
      return [];
    }
  }

  /**
   * Clean up orphaned files
   */
  async cleanupOrphanedFiles(): Promise<number> {
    try {
      const orphaned = await this.findOrphanedFiles();
      
      for (const file of orphaned) {
        await this.deleteFile(file.id);
      }
      
      console.log(`Cleaned up ${orphaned.length} orphaned files`);
      return orphaned.length;
    } catch (error) {
      console.error('Orphaned file cleanup failed:', error);
      return 0;
    }
  }

  /**
   * Get collection name for entity type
   */
  private getCollectionName(entityType: FileReference['entityType']): string {
    switch (entityType) {
      case 'user': return 'users';
      case 'deal': return 'dealCaptures';
      case 'loan': return 'consultantLoans';
      case 'development': return 'developments';
      case 'reservation': return 'reservations';
      default: return entityType;
    }
  }

  /**
   * Get storage statistics
   */
  async getStorageStats(): Promise<{
    totalFiles: number;
    totalOriginalSize: number;
    totalCompressedSize: number;
    averageCompressionRatio: number;
    filesByType: Record<string, number>;
    savingsAmount: number;
  }> {
    try {
      const snapshot = await getDocs(collection(db, 'fileReferences'));
      
      let totalOriginalSize = 0;
      let totalCompressedSize = 0;
      const filesByType: Record<string, number> = {};
      
      snapshot.docs.forEach(doc => {
        const data = doc.data() as FileReference;
        totalOriginalSize += data.originalSize;
        totalCompressedSize += data.compressedSize;
        
        const ext = data.filename.split('.').pop()?.toLowerCase() || 'unknown';
        filesByType[ext] = (filesByType[ext] || 0) + 1;
      });
      
      const averageCompressionRatio = snapshot.size > 0
        ? ((totalOriginalSize - totalCompressedSize) / totalOriginalSize) * 100
        : 0;
      
      return {
        totalFiles: snapshot.size,
        totalOriginalSize,
        totalCompressedSize,
        averageCompressionRatio,
        filesByType,
        savingsAmount: totalOriginalSize - totalCompressedSize
      };
    } catch (error) {
      console.error('Failed to get storage stats:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const fileManagementService = FileManagementService.getInstance();